/**
 * `Pill` — compact mono-text label. Six tones bound to the semantic
 * palette (CLAUDE.md §4). Copied shape from the dashboard's
 * `components/atoms/Pill.tsx` per ADR-0003 ("atoms stay app-local
 * until duplication is concrete").
 */

import type { ReactNode } from 'react';

import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";

export type PillTone = 'neutral' | 'lime' | 'amber' | 'coral' | 'sky' | 'mint';

export interface PillProps {
  tone?: PillTone;
  children: ReactNode;
}

export function Pill({ tone = 'neutral', children }: PillProps): JSX.Element {
  const C = useC();
  const tones: Record<PillTone, { bg: string; fg: string; bd: string }> = {
    neutral: { bg: C.surface2, fg: C.muted, bd: C.border },
    lime: { bg: C.limeDim, fg: C.limeFg, bd: 'rgba(217,255,63,0.3)' },
    amber: { bg: C.amberDim, fg: C.amberFg, bd: 'rgba(255,182,39,0.3)' },
    coral: { bg: C.coralDim, fg: C.coralFg, bd: 'rgba(255,107,71,0.3)' },
    sky: { bg: C.skyDim, fg: C.skyFg, bd: 'rgba(127,184,255,0.3)' },
    mint: { bg: C.mintDim, fg: C.mintFg, bd: 'rgba(90,255,205,0.3)' },
  };
  const t = tones[tone];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5"
      style={{
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        borderRadius: 2,
        fontFamily: monoFont,
        fontSize: 9,
        letterSpacing: 0.8,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </span>
  );
}

/**
 * Map an admin account status (or any free-text status string) to a
 * Pill tone. Closed enum is `active | past_due | suspended | closed`
 * per `AdminAccountUpdateBody.status`. Unknown values render neutral.
 */
export function statusTone(status: string): PillTone {
  switch (status) {
    case 'active':
      return 'mint';
    case 'past_due':
      return 'amber';
    case 'suspended':
    case 'closed':
      return 'coral';
    default:
      return 'neutral';
  }
}
